import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { Card, CardContent } from './card';
import { ChevronDown, Search, Globe, Check, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface CountryPhoneData {
  code: string;
  name: string;
  nameEn: string;
  continent: string;
  flag: string;
  dialCode: string;
  format?: string; // Format pattern for display
  pattern?: RegExp; // Validation pattern
}

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (countryCode: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  defaultCountry?: string;
}

const phoneCountries: CountryPhoneData[] = [
  // Europe
  { code: 'CZ', name: 'Česká republika', nameEn: 'Czech Republic', continent: 'Europe', flag: '🇨🇿', dialCode: '+420', format: 'XXX XXX XXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'SK', name: 'Slovensko', nameEn: 'Slovakia', continent: 'Europe', flag: '🇸🇰', dialCode: '+421', format: 'XXX XXX XXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'DE', name: 'Německo', nameEn: 'Germany', continent: 'Europe', flag: '🇩🇪', dialCode: '+49', format: 'XXX XXXXXXX', pattern: /^[1-9]\d{10,11}$/ },
  { code: 'AT', name: 'Rakousko', nameEn: 'Austria', continent: 'Europe', flag: '🇦🇹', dialCode: '+43', format: 'XXX XXXXXXX', pattern: /^[1-9]\d{8,12}$/ },
  { code: 'PL', name: 'Polsko', nameEn: 'Poland', continent: 'Europe', flag: '🇵🇱', dialCode: '+48', format: 'XXX XXX XXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'HU', name: 'Maďarsko', nameEn: 'Hungary', continent: 'Europe', flag: '🇭🇺', dialCode: '+36', format: 'XX XXX XXXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'SI', name: 'Slovinsko', nameEn: 'Slovenia', continent: 'Europe', flag: '🇸🇮', dialCode: '+386', format: 'XX XXX XXX', pattern: /^[1-9]\d{7,8}$/ },
  { code: 'HR', name: 'Chorvatsko', nameEn: 'Croatia', continent: 'Europe', flag: '🇭🇷', dialCode: '+385', format: 'XX XXX XXXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'IT', name: 'Itálie', nameEn: 'Italy', continent: 'Europe', flag: '🇮🇹', dialCode: '+39', format: 'XXX XXX XXXX', pattern: /^[1-9]\d{9,10}$/ },
  { code: 'FR', name: 'Francie', nameEn: 'France', continent: 'Europe', flag: '🇫🇷', dialCode: '+33', format: 'X XX XX XX XX', pattern: /^[1-9]\d{9}$/ },
  { code: 'ES', name: 'Španělsko', nameEn: 'Spain', continent: 'Europe', flag: '🇪🇸', dialCode: '+34', format: 'XXX XXX XXX', pattern: /^[6-9]\d{8}$/ },
  { code: 'PT', name: 'Portugalsko', nameEn: 'Portugal', continent: 'Europe', flag: '🇵🇹', dialCode: '+351', format: 'XXX XXX XXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'NL', name: 'Nizozemsko', nameEn: 'Netherlands', continent: 'Europe', flag: '🇳🇱', dialCode: '+31', format: 'X XXXX XXXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'BE', name: 'Belgie', nameEn: 'Belgium', continent: 'Europe', flag: '🇧🇪', dialCode: '+32', format: 'XXX XX XX XX', pattern: /^[1-9]\d{8}$/ },
  { code: 'LU', name: 'Lucembursko', nameEn: 'Luxembourg', continent: 'Europe', flag: '🇱🇺', dialCode: '+352', format: 'XXX XXX XXX', pattern: /^[1-9]\d{8}$/ },
  { code: 'CH', name: 'Švýcarsko', nameEn: 'Switzerland', continent: 'Europe', flag: '🇨🇭', dialCode: '+41', format: 'XX XXX XX XX', pattern: /^[1-9]\d{8}$/ },
  { code: 'DK', name: 'Dánsko', nameEn: 'Denmark', continent: 'Europe', flag: '🇩🇰', dialCode: '+45', format: 'XX XX XX XX', pattern: /^[1-9]\d{7}$/ },
  { code: 'SE', name: 'Švédsko', nameEn: 'Sweden', continent: 'Europe', flag: '🇸🇪', dialCode: '+46', format: 'XX XXX XX XX', pattern: /^[1-9]\d{8,9}$/ },
  { code: 'NO', name: 'Norsko', nameEn: 'Norway', continent: 'Europe', flag: '🇳🇴', dialCode: '+47', format: 'XXX XX XXX', pattern: /^[1-9]\d{7}$/ },
  { code: 'FI', name: 'Finsko', nameEn: 'Finland', continent: 'Europe', flag: '🇫🇮', dialCode: '+358', format: 'XX XXX XXXX', pattern: /^[1-9]\d{8,9}$/ },
  { code: 'GB', name: 'Velká Británie', nameEn: 'United Kingdom', continent: 'Europe', flag: '🇬🇧', dialCode: '+44', format: 'XXXX XXX XXXX', pattern: /^[1-9]\d{9,10}$/ },
  { code: 'IE', name: 'Irsko', nameEn: 'Ireland', continent: 'Europe', flag: '🇮🇪', dialCode: '+353', format: 'XX XXX XXXX', pattern: /^[1-9]\d{8}$/ },
  
  // Asia
  { code: 'CN', name: 'Čína', nameEn: 'China', continent: 'Asia', flag: '🇨🇳', dialCode: '+86', format: 'XXX XXXX XXXX', pattern: /^[1-9]\d{10}$/ },
  { code: 'JP', name: 'Japonsko', nameEn: 'Japan', continent: 'Asia', flag: '🇯🇵', dialCode: '+81', format: 'XX XXXX XXXX', pattern: /^[1-9]\d{9,10}$/ },
  { code: 'KR', name: 'Jižní Korea', nameEn: 'South Korea', continent: 'Asia', flag: '🇰🇷', dialCode: '+82', format: 'XX XXXX XXXX', pattern: /^[1-9]\d{9,10}$/ },
  { code: 'IN', name: 'Indie', nameEn: 'India', continent: 'Asia', flag: '🇮🇳', dialCode: '+91', format: 'XXXXX XXXXX', pattern: /^[6-9]\d{9}$/ },
  { code: 'SG', name: 'Singapur', nameEn: 'Singapore', continent: 'Asia', flag: '🇸🇬', dialCode: '+65', format: 'XXXX XXXX', pattern: /^[6-9]\d{7}$/ },
  { code: 'TR', name: 'Turecko', nameEn: 'Turkey', continent: 'Asia', flag: '🇹🇷', dialCode: '+90', format: 'XXX XXX XX XX', pattern: /^[5]\d{9}$/ },
  
  // North America
  { code: 'US', name: 'Spojené státy americké', nameEn: 'United States', continent: 'North America', flag: '🇺🇸', dialCode: '+1', format: '(XXX) XXX-XXXX', pattern: /^[2-9]\d{9}$/ },
  { code: 'CA', name: 'Kanada', nameEn: 'Canada', continent: 'North America', flag: '🇨🇦', dialCode: '+1', format: '(XXX) XXX-XXXX', pattern: /^[2-9]\d{9}$/ },
  { code: 'MX', name: 'Mexiko', nameEn: 'Mexico', continent: 'North America', flag: '🇲🇽', dialCode: '+52', format: 'XXX XXX XXXX', pattern: /^[1-9]\d{9}$/ },
  
  // South America
  { code: 'BR', name: 'Brazílie', nameEn: 'Brazil', continent: 'South America', flag: '🇧🇷', dialCode: '+55', format: 'XX XXXXX XXXX', pattern: /^[1-9]\d{10}$/ },
  { code: 'AR', name: 'Argentina', nameEn: 'Argentina', continent: 'South America', flag: '🇦🇷', dialCode: '+54', format: 'XX XXXX XXXX', pattern: /^[1-9]\d{9}$/ },
  
  // Oceania
  { code: 'AU', name: 'Austrálie', nameEn: 'Australia', continent: 'Oceania', flag: '🇦🇺', dialCode: '+61', format: 'XXX XXX XXX', pattern: /^[2-9]\d{8}$/ },
];

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  onCountryChange,
  placeholder = "Zadejte telefonní číslo...",
  className,
  disabled = false,
  defaultCountry = 'CZ'
}) => {
  const { i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneData | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize selected country
  useEffect(() => {
    const country = phoneCountries.find(c => c.code === defaultCountry);
    if (country && !selectedCountry) {
      setSelectedCountry(country);
      onCountryChange?.(country.code);
    }
  }, [defaultCountry, selectedCountry, onCountryChange]);

  // Parse existing phone number
  useEffect(() => {
    if (value && !phoneNumber) {
      // Try to extract country code and number from existing value
      for (const country of phoneCountries) {
        if (value.startsWith(country.dialCode)) {
          setSelectedCountry(country);
          setPhoneNumber(value.substring(country.dialCode.length).trim());
          onCountryChange?.(country.code);
          break;
        }
      }
      
      // If no country code found, assume it's just the number
      if (!selectedCountry && value) {
        setPhoneNumber(value);
      }
    }
  }, [value, phoneNumber, selectedCountry, onCountryChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format phone number according to country format
  const formatPhoneNumber = (number: string, country: CountryPhoneData): string => {
    const digits = number.replace(/\D/g, '');
    if (!country.format || !digits) return digits;

    let formatted = country.format;
    let digitIndex = 0;
    
    for (let i = 0; i < formatted.length && digitIndex < digits.length; i++) {
      if (formatted[i] === 'X') {
        formatted = formatted.substring(0, i) + digits[digitIndex] + formatted.substring(i + 1);
        digitIndex++;
      }
    }
    
    // Remove remaining X's if we don't have enough digits
    return formatted.substring(0, formatted.indexOf('X') === -1 ? formatted.length : formatted.indexOf('X'));
  };

  // Validate phone number
  const isValidPhoneNumber = (number: string, country: CountryPhoneData): boolean => {
    if (!country.pattern) return number.length > 0;
    const digits = number.replace(/\D/g, '');
    return country.pattern.test(digits);
  };

  // Handle phone number input change
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digits = inputValue.replace(/\D/g, '');
    
    setPhoneNumber(digits);
    
    if (selectedCountry) {
      const fullNumber = selectedCountry.dialCode + digits;
      onChange(fullNumber);
    } else {
      onChange(digits);
    }
  };

  // Handle country selection
  const handleCountrySelect = (country: CountryPhoneData) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery('');
    onCountryChange?.(country.code);
    
    // Update full phone number with new country code
    const fullNumber = phoneNumber ? country.dialCode + phoneNumber : country.dialCode;
    onChange(fullNumber);
  };

  // Filter countries based on search query
  const filteredCountries = phoneCountries.filter(country => {
    const query = searchQuery.toLowerCase();
    const currentLang = i18n.language;
    
    if (currentLang === 'cs') {
      return country.name.toLowerCase().includes(query) || 
             country.code.toLowerCase().includes(query) ||
             country.dialCode.includes(query);
    } else {
      return country.nameEn.toLowerCase().includes(query) || 
             country.code.toLowerCase().includes(query) ||
             country.dialCode.includes(query);
    }
  });

  // Group countries by continent
  const groupedCountries = filteredCountries.reduce((acc, country) => {
    if (!acc[country.continent]) {
      acc[country.continent] = [];
    }
    acc[country.continent].push(country);
    return acc;
  }, {} as Record<string, CountryPhoneData[]>);

  const displayNumber = selectedCountry && phoneNumber 
    ? formatPhoneNumber(phoneNumber, selectedCountry)
    : phoneNumber;

  const isValid = selectedCountry ? isValidPhoneNumber(phoneNumber, selectedCountry) : phoneNumber.length === 0;

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div className="flex">
        {/* Country selector */}
        <div
          className={cn(
            "flex items-center px-3 text-sm border border-r-0 rounded-l-md cursor-pointer bg-background h-12",
            "hover:border-gray-300 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500",
            disabled && "opacity-50 cursor-not-allowed bg-gray-50",
            !isValid && phoneNumber && "border-red-300"
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {selectedCountry && (
            <>
              <span className="mr-2 text-lg">{selectedCountry.flag}</span>
              <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
            </>
          )}
          <ChevronDown 
            className={cn(
              "h-4 w-4 text-gray-400 transition-transform ml-1",
              isOpen && "transform rotate-180"
            )} 
          />
        </div>

        {/* Phone number input */}
        <Input
          type="tel"
          value={displayNumber}
          onChange={handlePhoneNumberChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "rounded-l-none border-l-0 focus:ring-0 focus:border-orange-500 h-12",
            !isValid && phoneNumber && "border-red-300"
          )}
        />
      </div>

      {/* Validation message */}
      {phoneNumber && !isValid && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <Phone className="h-3 w-3" />
          Neplatné telefonní číslo pro vybranou zemi
        </p>
      )}

      {/* Country dropdown */}
      {isOpen && !disabled && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg">
          <CardContent className="p-0">
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Hledat zemi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {Object.keys(groupedCountries).length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <Search className="h-5 w-5 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm">Žádné výsledky</p>
                </div>
              ) : (
                Object.entries(groupedCountries).map(([continent, countries]) => (
                  <div key={continent} className="border-b last:border-b-0">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                      <Globe className="inline h-3 w-3 mr-1" />
                      {continent}
                    </div>
                    {countries.map((country) => (
                      <div
                        key={country.code}
                        className={cn(
                          "flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-slate-50",
                          selectedCountry?.code === country.code && "bg-orange-50 text-orange-600"
                        )}
                        onClick={() => handleCountrySelect(country)}
                      >
                        <span className="mr-3 text-lg">{country.flag}</span>
                        <div className="flex-1">
                          <div className="font-medium">
                            {i18n.language === 'cs' ? country.name : country.nameEn}
                          </div>
                          <div className="text-xs text-gray-500">
                            {country.dialCode}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 ml-2">
                          {country.code}
                        </span>
                        {selectedCountry?.code === country.code && (
                          <Check className="h-4 w-4 ml-2 text-orange-600" />
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PhoneNumberInput;